package diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme

import androidx.compose.runtime.Immutable
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDirection
import androidx.compose.ui.unit.em
import androidx.compose.ui.unit.sp

/**
 * Typography tokens — Public Sans scale (docs/DESIGN_SYSTEM.md). Access via `AppTheme.typography`.
 * Built from a [FontFamily] (Public Sans, supplied by [AppTheme] at runtime via [publicSansFamily]);
 * falls back to platform sans-serif for the CompositionLocal default / previews.
 */
@Immutable
data class AppTypography(
    val displayLg: TextStyle,
    val headlineLg: TextStyle,
    val headlineLgMobile: TextStyle,
    val headlineMd: TextStyle,
    val titleSm: TextStyle,
    val bodyLg: TextStyle,
    val bodyMd: TextStyle,
    val bodySm: TextStyle,
    val labelMd: TextStyle,
    val labelSm: TextStyle,
)

/**
 * [tracked] disables letter-spacing for scripts where tracking is wrong — Farsi joins glyphs, and
 * LOCALIZATION.md § Typography forbids tracking Vazirmatn (it breaks the joined letterforms).
 */
/**
 * Every style resolves its direction from the TEXT, not the layout ([TextDirection.Content]).
 * User-authored content is whatever language the author typed, so in a Farsi (RTL) layout an English
 * post used to have its trailing punctuation reordered to the front — "…Willowdale already!" rendered
 * as "!Willowdale already" — and vice-versa for a Farsi post read in English. Content direction fixes
 * that per paragraph and is a no-op for chrome strings, which are always in the active language.
 * Set here, once, so it cannot be forgotten on a new screen (CLAUDE.md § tokens, not literals).
 */
fun appTypography(appFontFamily: FontFamily, tracked: Boolean = true): AppTypography = AppTypography(
    displayLg = TextStyle(
        fontFamily = appFontFamily, textDirection = TextDirection.Content, fontWeight = FontWeight.Bold,
        fontSize = 40.sp, lineHeight = 48.sp,
        letterSpacing = if (tracked) (-0.02).em else 0.em,
    ),
    headlineLg = TextStyle(
        fontFamily = appFontFamily, textDirection = TextDirection.Content, fontWeight = FontWeight.Bold,
        fontSize = 28.sp, lineHeight = 36.sp,
    ),
    headlineLgMobile = TextStyle(
        fontFamily = appFontFamily, textDirection = TextDirection.Content, fontWeight = FontWeight.Bold,
        fontSize = 24.sp, lineHeight = 32.sp,
    ),
    headlineMd = TextStyle(
        fontFamily = appFontFamily, textDirection = TextDirection.Content, fontWeight = FontWeight.SemiBold,
        fontSize = 20.sp, lineHeight = 28.sp,
    ),
    // Compact-card titles: headline weight at body size, tight leading so 2-line titles stay short.
    titleSm = TextStyle(
        fontFamily = appFontFamily, textDirection = TextDirection.Content, fontWeight = FontWeight.SemiBold,
        fontSize = 15.sp, lineHeight = 20.sp,
    ),
    bodyLg = TextStyle(
        fontFamily = appFontFamily, textDirection = TextDirection.Content, fontWeight = FontWeight.Normal,
        fontSize = 18.sp, lineHeight = 28.sp,
    ),
    bodyMd = TextStyle(
        fontFamily = appFontFamily, textDirection = TextDirection.Content, fontWeight = FontWeight.Normal,
        fontSize = 16.sp, lineHeight = 24.sp,
    ),
    bodySm = TextStyle(
        fontFamily = appFontFamily, textDirection = TextDirection.Content, fontWeight = FontWeight.Normal,
        fontSize = 14.sp, lineHeight = 20.sp,
    ),
    labelMd = TextStyle(
        fontFamily = appFontFamily, textDirection = TextDirection.Content, fontWeight = FontWeight.SemiBold,
        fontSize = 14.sp, lineHeight = 16.sp,
        letterSpacing = if (tracked) 0.01.em else 0.em,
    ),
    labelSm = TextStyle(
        fontFamily = appFontFamily, textDirection = TextDirection.Content, fontWeight = FontWeight.Medium,
        fontSize = 12.sp, lineHeight = 16.sp,
        letterSpacing = if (tracked) 0.02.em else 0.em,
    ),
)
